/**
 * Generic presence tracker — ref-counted online status per stream per identity.
 *
 * Supports multi-tab: each subscribe increments the ref count, each
 * unsubscribe decrements it. An identity is considered online when
 * ref count > 0.
 *
 * ## Usage
 *
 * ```typescript
 * const presence = new PresenceTracker();
 *
 * // On SSE connect:
 * presence.add(game_id, player_id);
 *
 * // On SSE disconnect:
 * presence.remove(game_id, player_id);
 *
 * // Query:
 * presence.online(game_id); // Set<string>
 * ```
 */
export class PresenceTracker {
  private streams = new Map<string, Map<string, number>>();

  /** Increment ref count for an identity on a stream. */
  add(streamId: string, identity_id: string): void {
    if (!this.streams.has(streamId)) this.streams.set(streamId, new Map());
    const counts = this.streams.get(streamId)!;
    counts.set(identity_id, (counts.get(identity_id) ?? 0) + 1);
  }

  /** Decrement ref count. Removes the identity when count reaches 0. */
  remove(streamId: string, identity_id: string): void {
    const counts = this.streams.get(streamId);
    if (!counts) return;
    const n = (counts.get(identity_id) ?? 1) - 1;
    if (n <= 0) counts.delete(identity_id);
    else counts.set(identity_id, n);
    if (counts.size === 0) this.streams.delete(streamId);
  }

  /** Get the set of online identity IDs for a stream. */
  online(streamId: string): Set<string> {
    const counts = this.streams.get(streamId);
    return counts ? new Set(counts.keys()) : new Set();
  }

  /** Check if a specific identity is online for a stream. */
  isOnline(streamId: string, identity_id: string): boolean {
    return (this.streams.get(streamId)?.get(identity_id) ?? 0) > 0;
  }

  /** @deprecated use `online` — removal in the next major */
  get_online(streamId: string): Set<string> {
    return this.online(streamId);
  }

  /** @deprecated use `isOnline` — removal in the next major */
  is_online(streamId: string, identity_id: string): boolean {
    return this.isOnline(streamId, identity_id);
  }
}
